/* eslint-disable react/display-name */
import React, { useState, useMemo } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box, Container, Typography, Paper, Avatar, InputBase,
  IconButton, Button, Modal, CircularProgress, Chip, Checkbox,
  FormControlLabel, TextField, Alert, Tooltip
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import PersonIcon from '@mui/icons-material/Person'
import GroupIcon from '@mui/icons-material/Group'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import UpdateIcon from '@mui/icons-material/Update'
import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded'
import SignInsDialog from '../components/Security/SignInsDialog'
import { sessionUserId, timeAgo } from '../utils/authToken'

const GET_ADMINS = gql`
  query GetAdmins {
    admins {
      _id name phone email userType roles isActive lastLoginAt createdAt updatedAt
    }
  }
`
const CREATE_ADMIN = gql`
  mutation CreateVendor($vendorInput: VendorInput!) {
    createVendor(vendorInput: $vendorInput) {
      _id name phone email userType roles isActive createdAt
    }
  }
`
const EDIT_ADMIN = gql`
  mutation EditVendor($vendorInput: VendorInput!) {
    editVendor(vendorInput: $vendorInput) {
      _id name phone email userType roles isActive updatedAt
    }
  }
`
const DELETE_ADMIN = gql`
  mutation DeleteVendor($id: ID!) {
    deleteVendor(id: $id) { _id email isActive }
  }
`

const AVAILABLE_ROLES = [
  { id: 'Super_admin', label: 'Super Admin' },
  { id: 'Admin', label: 'Admin' },
  { id: 'Manager', label: 'Manager' },
  { id: 'Agent', label: 'Agent' },
  { id: 'Clerk', label: 'Clerk' },
  { id: 'User', label: 'User' }
]

const ROLE_LEVELS = { Super_admin: 6, Admin: 5, Manager: 4, Agent: 3, Clerk: 2, User: 1 }

const roleChipStyle = roles => {
  if (!roles || !roles.length) return { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' }
  if (roles.includes('Super_admin')) return { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' }
  if (roles.includes('Admin')) return { bg: '#faf5ff', color: '#7c3aed', border: '#e9d5ff' }
  if (roles.includes('Manager')) return { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' }
  if (roles.includes('Agent')) return { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' }
  return { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' }
}

const roleCheckboxBorder = roleId => {
  if (roleId === 'Super_admin') return '#dc2626'
  if (roleId === 'Admin') return '#7c3aed'
  if (roleId === 'Manager') return '#2563eb'
  if (roleId === 'Agent') return '#16a34a'
  if (roleId === 'Clerk') return '#d97706'
  return '#64748b'
}

const stringToColor = s => {
  if (!s) return '#94a3b8'
  let h = 0
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h)
  const p = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#22c55e', '#14b8a6', '#6366f1']
  return p[Math.abs(h) % p.length]
}

const getInitials = name => {
  if (!name) return 'A'
  const parts = name.trim().split(' ')
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name[0].toUpperCase()
}

const formatDate = dt => {
  if (!dt) return '—'
  const d = new Date(dt)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const getCurrentUserRoleLevel = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user-enatega') || '{}')
    if (u.userType === 'ADMIN' || u.userType === 'SUPER_ADMIN') return 6
    const r = AVAILABLE_ROLES.find(r => r.id.toUpperCase() === u.userType)
    return r ? ROLE_LEVELS[r.id] : 1
  } catch { return 1 }
}

function AdminModal({ admin, onClose, refetch }) {
  const isEdit = !!admin
  const [formData, setFormData] = useState({
    name: admin?.name || '',
    phone: admin?.phone || '',
    email: admin?.email || '',
    roles: admin?.roles || []
  })
  const [formError, setFormError] = useState('')
  const [success, setSuccess] = useState('')

  const [createAdmin, { loading: createLoading }] = useMutation(CREATE_ADMIN, {
    refetchQueries: [{ query: GET_ADMINS }],
    onCompleted: () => {
      setSuccess('Account created! Temporary password sent to their email.')
      setTimeout(() => { onClose(); refetch() }, 1800)
    },
    onError: err => setFormError(err.message)
  })

  const [editAdmin, { loading: editLoading }] = useMutation(EDIT_ADMIN, {
    refetchQueries: [{ query: GET_ADMINS }],
    onCompleted: () => {
      setSuccess('Admin updated successfully!')
      setTimeout(() => { onClose(); refetch() }, 1200)
    },
    onError: err => setFormError(err.message)
  })

  const loading = createLoading || editLoading

  const toggleRole = roleId => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter(r => r !== roleId)
        : [...prev.roles, roleId]
    }))
  }

  const handleSubmit = () => {
    if (!formData.name.trim()) { setFormError('Name is required'); return }
    if (!formData.email.trim()) { setFormError('Email is required'); return }
    if (!formData.roles.length) { setFormError('Select at least one role'); return }
    setFormError('')

    const highestRole = formData.roles.reduce((max, r) => (ROLE_LEVELS[r] || 0) > (ROLE_LEVELS[max] || 0) ? r : max, formData.roles[0])
    const userType = highestRole === 'Super_admin' ? 'ADMIN' : highestRole.toUpperCase()

    if (isEdit) {
      editAdmin({ variables: { vendorInput: { _id: admin._id, name: formData.name, phone: formData.phone, email: formData.email, userType, roles: formData.roles } } })
    } else {
      createAdmin({ variables: { vendorInput: { name: formData.name, phone: formData.phone, email: formData.email, userType, roles: formData.roles } } })
    }
  }

  const currentLevel = getCurrentUserRoleLevel()

  return (
    <Box sx={{ bgcolor: '#fff', borderRadius: '20px', width: '100%', maxWidth: 520, mx: 2, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', outline: 'none', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #3b0764 60%, #581c87 100%)', px: 3, pt: 3, pb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: stringToColor(formData.name || admin?.email), fontSize: '1.3rem', fontWeight: 700, border: '3px solid rgba(255,255,255,0.25)' }}>
              {getInitials(formData.name || admin?.name)}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#fff', lineHeight: 1.2 }}>
                {isEdit ? 'Edit User Profile' : 'Create New User'}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>
                {isEdit ? 'Update user information and permissions' : 'Add a new admin user to your team'}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' }, mt: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Timestamps (edit mode only) */}
        {isEdit && (
          <Box sx={{ display: 'flex', gap: 2, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <CalendarTodayIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }} />
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
                Created: {formatDate(admin?.createdAt)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <UpdateIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }} />
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
                Updated: {formatDate(admin?.updatedAt)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ px: 3, py: 3 }}>
        {formError && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.82rem' }}>{formError}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.82rem' }}>{success}</Alert>}

        {/* Full Name */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Full Name</Typography>
          <TextField
            fullWidth size="small"
            placeholder="Enter full name"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }}
          />
        </Box>

        {/* Phone */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phone Number</Typography>
          <TextField
            fullWidth size="small"
            placeholder="Enter phone number"
            value={formData.phone}
            onChange={e => setFormData({ ...formData, phone: e.target.value })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }}
          />
        </Box>

        {/* Email */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email Address</Typography>
          <TextField
            fullWidth size="small" type="email"
            placeholder="Enter email address"
            value={formData.email}
            onChange={e => setFormData({ ...formData, email: e.target.value })}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }}
          />
        </Box>

        {/* User Roles */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>User Roles</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            {AVAILABLE_ROLES.map(role => {
              const checked = formData.roles.includes(role.id)
              const disabled = ROLE_LEVELS[role.id] > currentLevel
              const borderColor = roleCheckboxBorder(role.id)
              return (
                <Box
                  key={role.id}
                  onClick={() => { if (!disabled) toggleRole(role.id) }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1.5, py: 1, borderRadius: '10px',
                    border: `1.5px solid ${checked ? borderColor : '#e2e8f0'}`,
                    bgcolor: checked ? `${borderColor}10` : '#f8fafc',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.45 : 1,
                    transition: 'all 0.15s ease'
                  }}
                >
                  <Checkbox
                    checked={checked}
                    size="small"
                    disabled={disabled}
                    onChange={() => {}}
                    sx={{ p: 0, color: borderColor, '&.Mui-checked': { color: borderColor } }}
                  />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: checked ? 600 : 500, color: checked ? borderColor : '#374151' }}>
                    {role.label}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>

        {/* Password info note */}
        <Box sx={{ mb: 2.5, p: 1.5, borderRadius: '10px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ fontSize: 16, color: '#3b82f6', flexShrink: 0 }}>ℹ</Box>
          <Typography sx={{ fontSize: '0.8rem', color: '#1e40af' }}>
            {isEdit
              ? 'Password cannot be changed from this page. User must reset via forgot password.'
              : 'A temporary password will be emailed to the user along with an email verification link.'}
          </Typography>
        </Box>

        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            onClick={onClose}
            sx={{ borderRadius: '10px', textTransform: 'none', color: '#64748b', fontWeight: 600, px: 2.5 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : (isEdit ? <EditIcon /> : <AddIcon />)}
            onClick={handleSubmit}
            disabled={loading}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, background: 'linear-gradient(135deg,#8b5cf6,#7c3aed)', '&:hover': { background: 'linear-gradient(135deg,#7c3aed,#6d28d9)' } }}>
            {loading ? 'Saving...' : (isEdit ? 'Update User' : 'Create User')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

function Admins() {
  const [searchQuery, setSearchQuery] = useState('')
  const [addModal, setAddModal] = useState(false)
  const [editTarget, setEditTarget] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  // Sign-ins and sign out of all devices (backend migration 137).
  const [signInsTarget, setSignInsTarget] = useState(null)
  const myId = sessionUserId()

  const { data, loading, error, refetch } = useQuery(GET_ADMINS, { fetchPolicy: 'network-only' })

  const [deleteAdmin, { loading: deleteLoading }] = useMutation(DELETE_ADMIN, {
    refetchQueries: [{ query: GET_ADMINS }],
    onCompleted: () => setDeleteTarget(null),
    onError: err => console.error('Delete admin error:', err.message)
  })

  const allAdmins = data?.admins ?? []
  const superAdmins = allAdmins.filter(a => a.roles?.includes('Super_admin') || a.userType === 'ADMIN').length
  const activeUsers = allAdmins.filter(a => a.isActive !== false).length

  const filtered = useMemo(() => {
    if (searchQuery.length < 2) return allAdmins
    const q = searchQuery.toLowerCase()
    return allAdmins.filter(a =>
      (a.name || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q) ||
      (a.phone || '').toLowerCase().includes(q)
    )
  }, [allAdmins, searchQuery])

  return (
    <>
      {/* Hero banner */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #3b0764 60%, #581c87 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AdminPanelSettingsIcon sx={{ color: '#c4b5fd', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15 }}>User Management</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Manage admin accounts and their permissions</Typography>
            </Box>
          </Box>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setAddModal(true)}
            sx={{ bgcolor: '#7c3aed', color: '#fff', fontWeight: 700, borderRadius: '12px', px: 2.5, py: 1.2, fontSize: '0.875rem', textTransform: 'none', '&:hover': { bgcolor: '#6d28d9' }, boxShadow: '0 4px 12px rgba(124,58,237,0.4)' }}>
            Create New User
          </Button>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        {/* Stat cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          {[
            { label: 'Total Admins', value: allAdmins.length, icon: <GroupIcon sx={{ color: '#7c3aed', fontSize: 26 }} />, bg: '#faf5ff', border: '#ddd6fe' },
            { label: 'Super Admins', value: superAdmins, icon: <AdminPanelSettingsIcon sx={{ color: '#dc2626', fontSize: 26 }} />, bg: '#fef2f2', border: '#fecaca' },
            { label: 'Active Users', value: activeUsers, icon: <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 26 }} />, bg: '#f0fdf4', border: '#bbf7d0' },
          ].map((card, i) => (
            <Paper key={i} elevation={0} sx={{ borderRadius: '16px', border: `1px solid ${card.border}`, p: 2.5, display: 'flex', alignItems: 'center', gap: 2, background: card.bg, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
              <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                {card.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                  {loading ? <CircularProgress size={20} /> : card.value}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b', mt: 0.3 }}>{card.label}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Table */}
        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
              All Admins{' '}
              <Typography component="span" sx={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 400 }}>({filtered.length})</Typography>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', px: 1.5, py: 0.8, width: 260 }}>
              <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
              <InputBase placeholder="Search admins..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} sx={{ fontSize: '0.875rem', flex: 1, color: '#475569' }} />
            </Box>
          </Box>

          {error && <Box sx={{ px: 3, py: 2, bgcolor: '#fef2f2' }}><Typography sx={{ fontSize: '0.83rem', color: '#dc2626' }}>Error: {error.message}</Typography></Box>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              {/* Table header */}
              <Box sx={{ minWidth: 900, display: 'grid', gridTemplateColumns: '2fr 2fr 1.4fr 1.4fr 1.1fr 1.2fr 132px', px: 3, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {['Name', 'Email', 'Phone', 'Roles', 'Created', 'Last login', 'Actions'].map((h, i) => (
                  <Typography key={i} sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</Typography>
                ))}
              </Box>

              {filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <AdminPanelSettingsIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                  <Typography sx={{ color: '#94a3b8' }}>No admins found</Typography>
                </Box>
              ) : filtered.map((admin, i) => {
                const rc = roleChipStyle(admin.roles)
                const primaryRole = admin.roles?.[0] || null
                return (
                  <Box key={admin._id} sx={{ minWidth: 900, display: 'grid', gridTemplateColumns: '2fr 2fr 1.4fr 1.4fr 1.1fr 1.2fr 132px', px: 3, py: 1.8, alignItems: 'center', borderBottom: i < filtered.length - 1 ? '1px solid #f1f5f9' : 'none', '&:hover': { bgcolor: '#fafafa' } }}>
                    {/* Name + Avatar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: stringToColor(admin.name), fontSize: '0.82rem', fontWeight: 700, flexShrink: 0 }}>
                        {getInitials(admin.name)}
                      </Avatar>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{admin.name || 'Unknown'}</Typography>
                    </Box>

                    {/* Email */}
                    <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>{admin.email || '—'}</Typography>

                    {/* Phone */}
                    <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>{admin.phone || '—'}</Typography>

                    {/* Role chip */}
                    {primaryRole ? (
                      <Chip
                        label={primaryRole}
                        size="small"
                        sx={{ bgcolor: rc.bg, color: rc.color, border: `1px solid ${rc.border}`, fontWeight: 700, fontSize: '0.7rem', height: 22, width: 'fit-content' }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>—</Typography>
                    )}

                    {/* Created */}
                    <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{formatDate(admin.createdAt)}</Typography>

                    {/* Last login (recorded from backend migration 137 on) */}
                    <Tooltip title={admin.lastLoginAt ? new Date(admin.lastLoginAt).toLocaleString() : 'No sign-in recorded yet'}>
                      <Typography sx={{ fontSize: '0.78rem', color: admin.lastLoginAt ? '#475569' : '#cbd5e1' }}>
                        {admin.lastLoginAt ? timeAgo(admin.lastLoginAt) : '—'}
                      </Typography>
                    </Tooltip>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Sign-ins">
                        <IconButton size="small" aria-label={`Sign-ins for ${admin.name || admin.email}`} onClick={() => setSignInsTarget(admin)} sx={{ color: '#475569', '&:hover': { bgcolor: '#f1f5f9' } }}>
                          <DevicesRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => setEditTarget(admin)} sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}>
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setDeleteTarget(admin)} sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}>
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          )}
        </Paper>
      </Container>

      {/* Add Modal */}
      <Modal open={addModal} onClose={() => setAddModal(false)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AdminModal admin={null} onClose={() => setAddModal(false)} refetch={refetch} />
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editTarget} onClose={() => setEditTarget(null)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AdminModal admin={editTarget} onClose={() => setEditTarget(null)} refetch={refetch} />
      </Modal>

      <SignInsDialog
        open={!!signInsTarget}
        onClose={() => setSignInsTarget(null)}
        account={signInsTarget}
        self={!!signInsTarget && String(signInsTarget._id) === String(myId)}
      />

      {/* Delete Confirmation Modal */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ bgcolor: '#fff', borderRadius: '16px', width: '100%', maxWidth: 400, mx: 2, p: 3, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', outline: 'none' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a', mb: 1 }}>Delete Admin</Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.875rem', mb: 3 }}>
            Are you sure you want to delete <strong>{deleteTarget?.name || deleteTarget?.email}</strong>? This cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => deleteAdmin({ variables: { id: deleteTarget._id } })}
              disabled={deleteLoading}
              sx={{ bgcolor: '#ef4444', textTransform: 'none', borderRadius: '10px', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>
              {deleteLoading ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}

export default withTranslation()(Admins)